import { useAuth } from './store/auth'

/*
 * Le client HTTP.
 *
 * Il fait deux choses que personne d'autre ne doit refaire : joindre le jeton
 * et joindre la venue courante. Un appel `fetch` écrit à la main quelque part
 * dans un composant finirait tôt ou tard par oublier `x-venue-id` et lirait
 * les données d'un autre lieu — ou, plus vraisemblablement, échouerait d'une
 * façon incompréhensible pour un utilisateur membre de deux lieux.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail?: unknown,
  ) {
    super(code)
    this.name = 'ApiError'
  }
}

type RequestOptions = {
  method?: string
  body?: unknown
  /** Les routes publiques ne portent ni jeton ni venue. */
  anonymous?: boolean
  signal?: AbortSignal
  /** Un lieu précis plutôt que le lieu courant : la console du studio règle
      un lieu sans y entrer. Le serveur applique ses règles d'accès comme
      pour l'en-tête ordinaire. */
  venueId?: string
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  if (!options.anonymous) {
    const { token, venueId, viewAs } = useAuth.getState()
    if (token) headers.Authorization = `Bearer ${token}`
    const venue = options.venueId ?? venueId
    if (venue) headers['x-venue-id'] = venue
    /* Le bridage volontaire du studio. Il ne peut que RETIRER des capacités :
       le serveur l'ignore pour qui n'est pas superadmin, et refuse toute
       écriture tant qu'il est posé. */
    if (viewAs) headers['x-view-as'] = viewAs
  }

  const response = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 401 && !options.anonymous) {
    /*
     * Le jeton est mort. On sort proprement plutôt que de laisser l'interface
     * enchaîner des 401 muets.
     *
     * Le CODE est conservé : `session_revoked` veut dire que le mot de passe a
     * changé ailleurs — sur un autre appareil, ou par une réinitialisation —
     * et l'écran de connexion peut alors l'expliquer. « Quelque chose s'est
     * mal passé » à la place transformerait une sécurité qui fonctionne en
     * panne apparente.
     */
    const code = await response
      .json()
      .then((body: { error?: string }) => body?.error ?? 'unauthenticated')
      .catch(() => 'unauthenticated')
    useAuth.getState().signOut(code === 'session_revoked' ? 'session_revoked' : null)
    throw new ApiError(401, code)
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (payload as { error?: string } | null)?.error ?? 'request_failed',
      (payload as { detail?: unknown } | null)?.detail,
    )
  }

  return payload as T
}

/**
 * Les en-têtes d'authentification, pour un `fetch` qui ne passe pas par `api`.
 *
 * Un téléchargement de fichier en a besoin : `api` décode du JSON, alors qu'un
 * classeur est un flux binaire. Et un `<a href>` n'emporte NI le jeton NI le
 * lieu — la route répond 401 et le navigateur enregistre la page d'erreur sous
 * le nom du fichier attendu, ce qui ne se voit qu'en l'ouvrant.
 */
export function authHeaders(): Record<string, string> {
  const { token, venueId, viewAs } = useAuth.getState()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  if (venueId) headers['x-venue-id'] = venueId
  if (viewAs) headers['x-view-as'] = viewAs
  return headers
}

/** Version pour la billetterie publique : ni jeton, ni tenancy. */
export function publicApi<T>(path: string, options: Omit<RequestOptions, 'anonymous'> = {}) {
  return api<T>(path, { ...options, anonymous: true })
}
